import React, {Component} from 'react';
import { connect } from 'react-redux';
import * as billingActions from '../actions/index';
import LoadingIndicator from '../../global/loading_indicator/loading_indicator';
import { loadStripe } from '@stripe/stripe-js';

let stripePromise = null;

class ProductSelector extends Component {

    state = {
        selections: {},
        submitting: false,
        fetchStarted: false
    }

    checkoutRef = React.createRef();
    embeddedCheckout = null;

    componentDidMount() {
        this.props.onFetchProducts();
        this.props.onFetchBillingDetails();
        this.setState({ fetchStarted: true });
    }

    componentDidUpdate(prevProps) {
        if (prevProps.loading_products && !this.props.loading_products && this.props.products.length > 0) {
            const selections = {};
            this.props.products.forEach(product => {
                const mostExpensivePrice = product.prices.reduce((max, p) =>
                    p.amount > max.amount ? p : max, product.prices[0]);
                selections[product.id] = {
                    enabled: true,
                    selectedPriceId: mostExpensivePrice.id
                };
            });
            this.setState({ selections });
        }

        if (this.props.checkout_client_secret && this.props.checkout_client_secret !== prevProps.checkout_client_secret) {
            this.mountEmbeddedCheckout(this.props.checkout_client_secret);
        }
    }

    componentWillUnmount() {
        if (this.embeddedCheckout) {
            this.embeddedCheckout.destroy();
        }
    }

    async mountEmbeddedCheckout(clientSecret) {
        if (!this.props.stripe_publishable) return;

        if (!stripePromise) {
            stripePromise = loadStripe(this.props.stripe_publishable);
        }

        const stripe = await stripePromise;
        if (this.embeddedCheckout) {
            this.embeddedCheckout.destroy();
        }

        this.embeddedCheckout = await stripe.initEmbeddedCheckout({
            clientSecret: clientSecret
        });

        if (this.checkoutRef.current) {
            this.embeddedCheckout.mount(this.checkoutRef.current);
        }
    }

    toggleProduct = (productId) => {
        this.setState(prev => ({
            selections: {
                ...prev.selections,
                [productId]: {
                    ...prev.selections[productId],
                    enabled: !prev.selections[productId].enabled
                }
            }
        }));
    }

    selectPrice = (productId, priceId) => {
        this.setState(prev => ({
            selections: {
                ...prev.selections,
                [productId]: {
                    ...prev.selections[productId],
                    selectedPriceId: priceId
                }
            }
        }));
    }

    handleContinue = () => {
        const items = [];
        Object.keys(this.state.selections).forEach(productId => {
            const sel = this.state.selections[productId];
            if (sel.enabled) {
                items.push({ price_id: sel.selectedPriceId, quantity: 1 });
            }
        });

        if (items.length === 0) {
            alert("Please select at least one app to continue.");
            return;
        }

        this.setState({ submitting: true });
        this.props.onCreateCheckout(items);
    }

    formatPrice(amount, interval) {
        const dollars = (amount / 100).toFixed(2).replace(/\.00$/, '');
        if (interval) {
            return `$${dollars}/${interval}`;
        }
        return `$${dollars}`;
    }

    render() {
        if (this.props.loading_products || !this.state.fetchStarted) {
            return <LoadingIndicator />;
        }

        if (!this.props.products || this.props.products.length === 0) {
            return null;
        }

        // Show embedded checkout once we have a client secret
        if (this.props.checkout_client_secret) {
            return (
                <div className="w-full" style={{ minHeight: 'calc(100vh - 60px)' }}>
                    <div ref={this.checkoutRef} />
                </div>
            );
        }

        const hasSelection = Object.values(this.state.selections).some(s => s.enabled);

        return (
            <div className="max-w-2xl mx-auto py-8 px-4">
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">
                    Choose Your Apps
                </h1>
                <p className="text-gray-500 dark:text-gray-400 mb-8">
                    Select the apps you'd like to use. Each includes a 30-day free trial.
                </p>

                <div className="space-y-3 mb-8" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    {this.props.products.map(product => {
                        const sel = this.state.selections[product.id];
                        if (!sel) return null;
                        const isEnabled = sel.enabled;
                        const imageUrl = product.images && product.images.length > 0 ? product.images[0] : null;

                        return (
                            <div
                                key={product.id}
                                className={
                                    "rounded-lg border-2 p-5 transition-all " +
                                    (isEnabled
                                        ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-400"
                                        : "border-gray-200 bg-white dark:bg-gray-800 dark:border-gray-700 opacity-60")
                                }
                            >
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={isEnabled}
                                        onChange={() => this.toggleProduct(product.id)}
                                        className="shrink-0 h-5 w-5 text-blue-500 border-gray-300 rounded-sm cursor-pointer"
                                    />
                                    <div className="flex items-center ml-4 flex-1 min-w-0">
                                        {imageUrl && (
                                            <img
                                                src={imageUrl}
                                                alt={product.name}
                                                className="w-12 h-12 rounded-lg shrink-0 mr-4"
                                            />
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                                                {product.name}
                                            </h3>
                                            {product.description && (
                                                <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                                    {product.description}
                                                </p>
                                            )}
                                        </div>

                                        {isEnabled && product.prices.length > 1 && (
                                            <select
                                                value={sel.selectedPriceId}
                                                onChange={(e) => this.selectPrice(product.id, e.target.value)}
                                                className="shrink-0 ml-4 rounded-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-xs focus:border-blue-500 focus:ring-blue-500 text-base py-2 px-3"
                                            >
                                                {product.prices
                                                    .sort((a, b) => b.amount - a.amount)
                                                    .map(price => (
                                                        <option key={price.id} value={price.id}>
                                                            {price.nickname || price.id} — {this.formatPrice(price.amount, price.interval)}
                                                        </option>
                                                    ))
                                                }
                                            </select>
                                        )}

                                        {isEnabled && product.prices.length === 1 && (
                                            <span className="shrink-0 ml-4 text-base font-medium text-gray-700 dark:text-gray-300">
                                                {this.formatPrice(product.prices[0].amount, product.prices[0].interval)}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <button
                    onClick={this.handleContinue}
                    disabled={!hasSelection || this.state.submitting}
                    className={
                        "w-full py-4 px-6 rounded-lg text-lg font-semibold text-white shadow-lg transition-all " +
                        (hasSelection && !this.state.submitting
                            ? "bg-blue-500 hover:bg-blue-600 active:bg-blue-700 cursor-pointer"
                            : "bg-gray-300 dark:bg-gray-600 cursor-not-allowed")
                    }
                >
                    {this.state.submitting ? 'Setting up checkout...' : 'Continue'}
                </button>
            </div>
        );
    }
}

const mapStateToProps = state => {
    return {
        loading_products: state.billing.loading_products,
        products: state.billing.products,
        checkout_client_secret: state.billing.checkout_client_secret,
        stripe_publishable: state.billing.stripe_publishable,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onFetchProducts: () => dispatch(billingActions.fetchProducts()),
        onFetchBillingDetails: () => dispatch(billingActions.fetchBillingDetails()),
        onCreateCheckout: (items) => dispatch(billingActions.createCheckout(items)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductSelector);
